import { useMemo, useEffect, useRef } from 'react'
import { type CompleteEvent, Model } from 'survey-core'
import { Survey } from 'survey-react-ui'
import { Box } from '@mui/material'

interface Props {
  surveyJson: any
  onComplete: (survey: Model, options: CompleteEvent) => void
  onLoad?: (survey: Model) => void
  /**
   * Synchronous model configuration applied before the first paint (theme, panelless,
   * hidden title/nav, answers). Kept separate from onLoad — which sets React state —
   * so the survey never flashes its unconfigured default.
   */
  configureModel?: (survey: Model) => void
}

export function SurveyDisplay ({
  surveyJson,
  onComplete,
  onLoad,
  configureModel
}: Props): JSX.Element {
  // configureModel transitively depends on the parent's surveyModel state, so it must
  // NOT be a useMemo dependency (that would rebuild the model every time the parent sets
  // surveyModel — an infinite loop). Read the latest via a ref instead: the model is
  // rebuilt only when the schema (surveyJson) or onComplete changes.
  const configureRef = useRef(configureModel)
  configureRef.current = configureModel

  const surveyModel = useMemo(() => {
    const model = new Model(surveyJson)

    model.onComplete.add(onComplete)
    configureRef.current?.(model)

    return model
  }, [surveyJson, onComplete])

  useEffect(() => {
    onLoad?.(surveyModel)
  }, [surveyModel, onLoad])

  return (
    <Box sx={{ width: '100%' }}>
      <Survey model={surveyModel} />
    </Box>
  )
}
